import {
	type AddOrderQuoteOfferSampleInput,
	type GetOrderQuoteOfferBatchesQuery,
	type UpdateOrderQuoteOfferSampleInput,
	useAddOrderQuoteOfferSampleMutation,
	useUpdateOrderQuoteOfferSampleMutation,
	useRemoveOrderQuoteOfferSampleMutation,
	SamplePaymentTreatment,
} from '@codezo/graphql';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CurrencyText } from '../../CurrencyText';
import { Button } from '../../ui/button';
import { CurrencyInput } from '../../ui/currency-input';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../ui/dialog';
import { Label } from '../../ui/label';
import { NumberInput } from '../../ui/number-input';
import { Textarea } from '../../ui/textarea';
import { SampleTreatmentPicker } from '../../SampleTreatmentPicker';
import { useCurrencyStore } from '../../../stores/currency';
import {
	bestSampleRow,
	SampleCatalogPicker,
	sampleRowFill,
	useCatalogSamples,
	type SampleFill,
} from '../../pricing/SampleCatalogPicker';

export function EditSampleModal({
	open,
	onOpenChange,
	offerId,
	productId,
	vetterEntityId,
	existing,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	offerId: string;
	/** The offer's catalogue product. Null on a custom line, which has no catalogue prices. */
	productId?: string | null;
	/** The offer's supplier, which decides which catalogue prices are usable and the best match. */
	vetterEntityId?: string | null;
	existing: GetOrderQuoteOfferBatchesQuery['getOrderQuoteOfferBatches'][number]['offers'][number]['sample'];
	onSuccess: () => void;
}) {
	const { selectedCurrency: currency } = useCurrencyStore();
	const [addSample] = useAddOrderQuoteOfferSampleMutation();
	const [updateSample] = useUpdateOrderQuoteOfferSampleMutation();
	const [removeSample] = useRemoveOrderQuoteOfferSampleMutation();

	const [baseSamplePrice, setBaseSamplePrice] = useState('');
	const [baseShippingPrice, setBaseShippingPrice] = useState('');
	const [minProductionDays, setMinProductionDays] = useState('');
	const [maxProductionDays, setMaxProductionDays] = useState('');
	const [deliveryDaysMin, setDeliveryDaysMin] = useState('');
	const [deliveryDaysMax, setDeliveryDaysMax] = useState('');
	const [notes, setNotes] = useState('');
	// New samples are PREPAID by default; an existing one keeps what it was saved
	// as. PREPAID and WAIVED both cost the client nothing, but differ in cash
	// flow — a waived sample never reaches an invoice at all.
	const [paymentTreatment, setPaymentTreatment] = useState<SamplePaymentTreatment>(
		SamplePaymentTreatment.Prepaid
	);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (open && existing) {
			setBaseSamplePrice(
				existing.baseSamplePrice != null ? String(existing.baseSamplePrice) : ''
			);
			setBaseShippingPrice(
				existing.baseShippingPrice != null ? String(existing.baseShippingPrice) : ''
			);
			setMinProductionDays(
				existing.minProductionDays != null ? String(existing.minProductionDays) : ''
			);
			setMaxProductionDays(
				existing.maxProductionDays != null ? String(existing.maxProductionDays) : ''
			);
			setDeliveryDaysMin(
				existing.deliveryDaysMin != null ? String(existing.deliveryDaysMin) : ''
			);
			setDeliveryDaysMax(
				existing.deliveryDaysMax != null ? String(existing.deliveryDaysMax) : ''
			);
			setNotes(existing.notes ?? '');
			setPaymentTreatment(existing.paymentTreatment);
		} else if (open) {
			setBaseSamplePrice('');
			setBaseShippingPrice('');
			setMinProductionDays('');
			setMaxProductionDays('');
			setDeliveryDaysMin('');
			setDeliveryDaysMax('');
			setNotes('');
			setPaymentTreatment(SamplePaymentTreatment.Prepaid);
		}
	}, [open, existing]);

	/* Every catalogue sample price on the product, in the form's currency. Shown
	   when editing too, so an existing sample can be re-priced from it. */
	const catalogSamples = useCatalogSamples(productId, currency, !open);

	/**
	 * Writes a catalogue price into the form. Only what the catalogue states, and
	 * with `onlyEmpty` only into fields still blank — the automatic fill must not
	 * replace anything typed while the catalogue was loading. A row picked by
	 * hand replaces the price fields it states.
	 */
	const applyFill = (f: SampleFill, onlyEmpty = false) => {
		const put = (value: number | null, current: string, set: (v: string) => void) => {
			if (value == null || (onlyEmpty && current !== '')) return;
			set(String(value));
		};
		put(f.baseSamplePrice, baseSamplePrice, setBaseSamplePrice);
		put(f.baseShippingPrice, baseShippingPrice, setBaseShippingPrice);
		put(f.minProductionDays, minProductionDays, setMinProductionDays);
		put(f.maxProductionDays, maxProductionDays, setMaxProductionDays);
		put(f.deliveryDaysMin, deliveryDaysMin, setDeliveryDaysMin);
		put(f.deliveryDaysMax, deliveryDaysMax, setDeliveryDaysMax);
	};

	/* Adding a sample fills the empty form from the best match: the supplier's
	   own sample price, else the default. Never when editing — that sample
	   already has a price — and never over a figure the admin typed. */
	useEffect(() => {
		if (existing || baseSamplePrice !== '') return;
		const best = bestSampleRow(catalogSamples, vetterEntityId);
		const fill = best ? sampleRowFill(best, currency) : null;
		if (fill) applyFill(fill, true);
		// Keyed on the rows' ids: the array is rebuilt every render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [catalogSamples.map((r) => r.id).join()]);

	/* Sample total preview — the sample and its freight, which is all there is
	   to charge. The admin fee that used to be a third term is gone: the price
	   below is entered whole rather than marked up on the way out. */
	const sampleTotal = (parseFloat(baseSamplePrice) || 0) + (parseFloat(baseShippingPrice) || 0);
	const hasSampleTotal = sampleTotal > 0;
	const isWaived = paymentTreatment === SamplePaymentTreatment.Waived;

	const handleSubmit = async () => {
		// A price is still required to charge for something; a waived sample has
		// nothing to charge, so it saves without one.
		if (!isWaived && !baseSamplePrice) return;
		setLoading(true);
		try {
			if (existing) {
				const input: UpdateOrderQuoteOfferSampleInput = {
					// Omitted entirely when waived — the fields are hidden, so sending
					// whatever they last held would store a price nobody can see.
					baseSamplePrice: isWaived
						? undefined
						: parseFloat(baseSamplePrice) || undefined,
					baseShippingPrice:
						!isWaived && baseShippingPrice ? parseFloat(baseShippingPrice) : undefined,
					currency,
					minProductionDays: minProductionDays ? parseInt(minProductionDays) : undefined,
					maxProductionDays: maxProductionDays ? parseInt(maxProductionDays) : undefined,
					deliveryDaysMin: deliveryDaysMin ? parseInt(deliveryDaysMin) : undefined,
					deliveryDaysMax: deliveryDaysMax ? parseInt(deliveryDaysMax) : undefined,
					notes: notes || undefined,
					paymentTreatment,
				};
				await updateSample({ variables: { id: existing.id, input } });
			} else {
				const input: AddOrderQuoteOfferSampleInput = {
					orderQuoteOfferId: offerId,
					// Optional since slice 8; omitted when waived.
					baseSamplePrice: isWaived ? undefined : parseFloat(baseSamplePrice),
					baseShippingPrice:
						!isWaived && baseShippingPrice ? parseFloat(baseShippingPrice) : undefined,
					currency,
					minProductionDays: minProductionDays ? parseInt(minProductionDays) : undefined,
					maxProductionDays: maxProductionDays ? parseInt(maxProductionDays) : undefined,
					deliveryDaysMin: deliveryDaysMin ? parseInt(deliveryDaysMin) : undefined,
					deliveryDaysMax: deliveryDaysMax ? parseInt(deliveryDaysMax) : undefined,
					notes: notes || undefined,
					paymentTreatment,
				};
				await addSample({ variables: { input } });
			}
			toast.success(existing ? 'Sample updated' : 'Sample added');
			onOpenChange(false);
			onSuccess();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to save sample');
		} finally {
			setLoading(false);
		}
	};

	const handleRemove = async () => {
		if (!existing) return;
		setLoading(true);
		try {
			await removeSample({ variables: { id: existing.id } });
			toast.success('Sample removed');
			onOpenChange(false);
			onSuccess();
		} catch {
			toast.error('Failed to remove sample');
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>{existing ? 'Edit sample' : 'Add sample'}</DialogTitle>
				</DialogHeader>
				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-3">
							{/* Above the prices, because it decides whether there are any.
							    CHARGED and PREPAID quote the client the same figure and
							    differ only in what happens afterwards; WAIVED removes
							    them entirely. */}
							<SampleTreatmentPicker
								label="Sample payment"
								value={paymentTreatment}
								onChange={setPaymentTreatment}
							/>

							{!isWaived && (
								<SampleCatalogPicker
									rows={catalogSamples}
									assignedVetterId={vetterEntityId}
									formCurrency={currency}
									current={{
										samplePrice:
											baseSamplePrice === ''
												? null
												: parseFloat(baseSamplePrice),
										shippingPrice:
											baseShippingPrice === ''
												? null
												: parseFloat(baseShippingPrice),
									}}
									onPick={(f) => applyFill(f)}
								/>
							)}

							{/* A waived sample has no price to set: the price maps feed exactly
							    one thing, what the client pays, and nobody is paying. The
							    backend stopped requiring one. */}
							{!isWaived && (
								<div className="grid grid-cols-2 gap-2">
									<div className="flex flex-col gap-1">
										<Label>
											Sample price <span className="text-destructive">*</span>
										</Label>
										<CurrencyInput
											currency={currency}
											value={baseSamplePrice}
											onChange={(e) =>
												setBaseSamplePrice(
													(e.target as HTMLInputElement).value
												)
											}
											placeholder="0.00"
											min={0}
											step="any"
										/>
									</div>
									<div className="flex flex-col gap-1">
										<Label>Shipping price</Label>
										<CurrencyInput
											currency={currency}
											value={baseShippingPrice}
											onChange={(e) =>
												setBaseShippingPrice(
													(e.target as HTMLInputElement).value
												)
											}
											placeholder="0.00"
											min={0}
											step="any"
										/>
									</div>
								</div>
							)}

							{/* Only when there is something to charge. A waived sample has
							    no prices to sum, and totalPrice now returns 0 server-side,
							    so there is nothing left to compensate for here. */}
							{!isWaived && hasSampleTotal && (
								<div className="flex items-center justify-between rounded-lg border border-line-1 bg-surface-fill/50 px-3 py-2">
									<div className="flex flex-col">
										<span className="text-2xs text-text-4">Sample total</span>
										<span className="text-2xs text-text-4">
											Sample + shipping
										</span>
									</div>
									<CurrencyText
										amount={sampleTotal}
										currency={currency}
										className="text-base font-semibold text-text-1"
									/>
								</div>
							)}

							<div className="grid grid-cols-2 gap-2">
								<div className="flex flex-col gap-1">
									<Label>Min production days</Label>
									<NumberInput
										value={minProductionDays}
										onChange={(e) =>
											setMinProductionDays(
												(e.target as HTMLInputElement).value
											)
										}
										placeholder="—"
										min={0}
									/>
								</div>
								<div className="flex flex-col gap-1">
									<Label>Max production days</Label>
									<NumberInput
										value={maxProductionDays}
										onChange={(e) =>
											setMaxProductionDays(
												(e.target as HTMLInputElement).value
											)
										}
										placeholder="—"
										min={0}
									/>
								</div>
								<div className="flex flex-col gap-1">
									<Label>Min delivery days</Label>
									<NumberInput
										value={deliveryDaysMin}
										onChange={(e) =>
											setDeliveryDaysMin((e.target as HTMLInputElement).value)
										}
										placeholder="—"
										min={0}
									/>
								</div>
								<div className="flex flex-col gap-1">
									<Label>Max delivery days</Label>
									<NumberInput
										value={deliveryDaysMax}
										onChange={(e) =>
											setDeliveryDaysMax((e.target as HTMLInputElement).value)
										}
										placeholder="—"
										min={0}
									/>
								</div>
							</div>
							<div className="flex flex-col gap-1">
								<Label>Notes</Label>
								<Textarea
									value={notes}
									onChange={(e) => setNotes(e.target.value)}
									placeholder="Optional"
									className="min-h-15"
									rows={2}
								/>
							</div>
						</div>
					</DialogBody>
					<DialogFooter>
						{existing && (
							<button
								type="button"
								onClick={handleRemove}
								disabled={loading}
								className="mr-auto text-xs text-status-danger transition-colors hover:text-status-danger/80"
							>
								Remove sample
							</button>
						)}
						<Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
							Cancel
						</Button>
						<Button type="submit" disabled={(!isWaived && !baseSamplePrice) || loading}>
							{loading ? 'Saving...' : existing ? 'Update' : 'Add'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
